import { useEffect, useState } from 'react';
import {
  ActivityContent,
  ActivityId,
  getContentApiBaseUrl,
  getActivityContent,
  getPhotos,
  getVideos,
} from '@/services/contentApi';

interface ActivityCmsFallback {
  images: string[];
  videoIds: string[];
}

interface ActivityCmsResult {
  images: string[];
  videoIds: string[];
  content: ActivityContent | null;
}

function sameArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Loads CMS-managed photos, videos, and bilingual text for an activity.
 * Falls back to the hardcoded defaults from activities.ts when the content API
 * is not configured, returns nothing, or fails (transient errors keep defaults).
 * Photos/videos refresh periodically so admin edits appear without a redeploy.
 */
export function useActivityCms(
  activityId: string,
  fallback: ActivityCmsFallback,
  pollMs = 300000
): ActivityCmsResult {
  const [images, setImages] = useState<string[]>(fallback.images);
  const [videoIds, setVideoIds] = useState<string[]>(fallback.videoIds);
  const [content, setContent] = useState<ActivityContent | null>(null);

  useEffect(() => {
    if (!getContentApiBaseUrl()) return;
    const id = activityId as ActivityId;
    let cancelled = false;

    const load = () => {
      getPhotos(id).then((p) => {
        if (!cancelled && p.length > 0) {
          setImages((prev) => (sameArray(prev, p) ? prev : p));
        }
      });
      getVideos(id).then((v) => {
        if (!cancelled && v.length > 0) {
          setVideoIds((prev) => (sameArray(prev, v) ? prev : v));
        }
      });
      getActivityContent(id).then((c) => {
        if (!cancelled && c) {
          setContent((prev) => (JSON.stringify(prev) === JSON.stringify(c) ? prev : c));
        }
      });
    };

    load();
    const interval = pollMs > 0 ? setInterval(load, pollMs) : undefined;
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [activityId, pollMs]);

  return { images, videoIds, content };
}
